"use client";

import type { Order, OrderItem } from "@/types/prisma";
import { ChevronDown, ChevronUp, PackageCheck, Search, Truck, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AdminPagination } from "@/components/admin/admin-pagination";
import { formatPrice } from "@/lib/utils";

/** `Prisma.OrderGetPayload<{ include: { items, user } }>`, written out. */
type AdminOrder = Order & {
  items: OrderItem[];
  user: { name: string | null; email: string } | null;
};

const ALL_STATUSES: AdminOrder["status"][] = [
  "PENDING","PAID","PROCESSING","PACKED","SHIPPED","OUT_FOR_DELIVERY","DELIVERED","CANCELLED","RETURNED"
];

const PER_PAGE = 10;

function statusTone(status: AdminOrder["status"]): "success" | "deal" | "prime" | "default" {
  if (status === "DELIVERED") return "success";
  if (status === "CANCELLED" || status === "RETURNED") return "deal";
  return "prime";
}

export function OrderManager({ initialOrders }: { initialOrders: AdminOrder[] }) {
  const [orders, setOrders] = useState(initialOrders);
  useEffect(() => setOrders(initialOrders), [initialOrders]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<AdminOrder["status"] | "ALL">("ALL");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      const matchesSearch =
        !q ||
        o.id.toLowerCase().includes(q) ||
        (o.user?.name ?? "").toLowerCase().includes(q) ||
        (o.user?.email ?? "").toLowerCase().includes(q) ||
        o.shippingPhone.includes(q) ||
        o.shippingFullName.toLowerCase().includes(q);
      const matchesStatus = statusFilter === "ALL" || o.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [orders, search, statusFilter]);

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  async function updateStatus(orderId: string, status: AdminOrder["status"]) {
    const previous = orders;
    setOrders((items) => items.map((o) => (o.id === orderId ? { ...o, status } : o)));
    try {
      const res = await fetch(`/api/admin/orders/${orderId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status })
      });
      if (!res.ok) throw new Error("Unable to update order status.");
      toast.success("Order status updated");
    } catch (error) {
      setOrders(previous);
      toast.error(error instanceof Error ? error.message : "Unable to update order status.");
    }
  }

  if (orders.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
        <p className="font-bold">No orders yet</p>
        <p className="mt-1 text-sm text-slate-500">Orders placed by customers will show up here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <PackageCheck className="h-6 w-6 text-amazon-teal" />
          <p className="mt-3 text-xs font-black uppercase text-slate-500">Total orders</p>
          <p className="mt-1 text-3xl font-black tracking-normal">{orders.length}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <Truck className="h-6 w-6 text-amazon-orange" />
          <p className="mt-3 text-xs font-black uppercase text-slate-500">Shipping queue</p>
          <p className="mt-1 text-3xl font-black tracking-normal">
            {orders.filter((o) => ["PROCESSING","PAID","PACKED"].includes(o.status)).length}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <p className="text-xs font-black uppercase text-slate-500">Revenue</p>
          <p className="mt-3 text-3xl font-black tracking-normal">
            {formatPrice(orders.reduce((sum, o) => sum + o.total, 0))}
          </p>
        </div>
      </div>

      {/* Search + filter */}
      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-48 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by order ID, customer, phone…"
            className="h-10 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
          />
          {search && (
            <button type="button" onClick={() => { setSearch(""); setPage(1); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as typeof statusFilter); setPage(1); }}
          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
        >
          <option value="ALL">All statuses</option>
          {ALL_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <p className="flex items-center text-sm text-slate-500">
          {filtered.length} result{filtered.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="bg-slate-100 text-xs uppercase text-slate-500 dark:bg-white/10">
              <tr>
                <th className="w-8 px-4 py-3" />
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Update</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-white/10">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">No orders found</td>
                </tr>
              ) : (
                paginated.flatMap((order) => {
                  const isExpanded = expandedId === order.id;
                  return [
                    <tr key={order.id} className="hover:bg-slate-50 dark:hover:bg-white/5">
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : order.id)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10"
                          title={isExpanded ? "Collapse" : "Show details"}
                        >
                          {isExpanded
                            ? <ChevronUp className="h-4 w-4" />
                            : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <p className="max-w-[120px] truncate font-black text-slate-950 dark:text-white">{order.id}</p>
                        <p className="text-xs text-slate-500">{new Date(order.createdAt).toLocaleDateString()}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-bold">{order.user?.name ?? order.shippingFullName}</p>
                        <p className="text-xs text-slate-500">{order.user?.email ?? "—"}</p>
                        <p className="text-xs text-slate-500">{order.shippingPhone}</p>
                        <p className="text-xs text-slate-400">
                          {order.shippingLine1}
                          {order.shippingLine2 ? `, ${order.shippingLine2}` : ""}
                          {[order.shippingCity, order.shippingState, order.shippingPincode]
                            .filter(Boolean)
                            .join(", ")
                            ? `, ${[order.shippingCity, order.shippingState, order.shippingPincode].filter(Boolean).join(", ")}`
                            : ""}
                        </p>
                      </td>
                      <td className="px-4 py-3">{order.items.length}</td>
                      <td className="px-4 py-3 font-bold">{formatPrice(order.total)}</td>
                      <td className="px-4 py-3">
                        <Badge tone={statusTone(order.status)}>{order.status}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={order.status}
                          onChange={(e) => updateStatus(order.id, e.target.value as AdminOrder["status"])}
                          className="h-9 rounded-md border border-slate-200 bg-transparent px-2 text-sm outline-none focus:border-amazon-orange dark:border-white/10"
                        >
                          {ALL_STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>,

                    isExpanded && (
                      <tr key={`${order.id}-detail`} className="bg-slate-50 dark:bg-white/[0.02]">
                        <td colSpan={7} className="px-6 py-5">
                          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">

                            {/* Shipping address */}
                            <div>
                              <p className="mb-2 text-xs font-black uppercase tracking-wider text-slate-400">
                                Shipping address
                              </p>
                              <p className="font-bold text-slate-900 dark:text-white">{order.shippingFullName}</p>
                              <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">{order.shippingLine1}</p>
                              {order.shippingLine2 && (
                                <p className="text-sm text-slate-600 dark:text-slate-300">{order.shippingLine2}</p>
                              )}
                              {[order.shippingCity, order.shippingState, order.shippingPincode]
                                .filter(Boolean)
                                .join(", ") && (
                                <p className="text-sm text-slate-600 dark:text-slate-300">
                                  {[order.shippingCity, order.shippingState, order.shippingPincode]
                                    .filter(Boolean)
                                    .join(", ")}
                                </p>
                              )}
                              <p className="mt-1.5 flex items-center gap-1.5 text-sm font-bold text-slate-700 dark:text-slate-200">
                                <span>📞</span>
                                {order.shippingPhone}
                              </p>
                            </div>

                            {/* Financials */}
                            <div>
                              <p className="mb-2 text-xs font-black uppercase tracking-wider text-slate-400">
                                Financials
                              </p>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Subtotal</span>
                                  <span>{formatPrice(order.subtotal)}</span>
                                </div>
                                {order.discount > 0 && (
                                  <div className="flex justify-between text-amazon-green">
                                    <span>Discount</span>
                                    <span>-{formatPrice(order.discount)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Shipping</span>
                                  <span>{order.shipping === 0 ? "FREE" : formatPrice(order.shipping)}</span>
                                </div>
                                {order.tax > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Tax</span>
                                    <span>{formatPrice(order.tax)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between border-t pt-1.5 font-bold dark:border-white/10">
                                  <span>Total</span>
                                  <span>{formatPrice(order.total)}</span>
                                </div>
                              </div>
                              <p className="mt-2 text-xs text-slate-400">
                                Payment:{" "}
                                <span className="font-bold text-slate-600 dark:text-slate-300">
                                  {order.paymentStatus}
                                </span>
                                {" · "}
                                {order.paymentProvider}
                              </p>
                            </div>

                            {/* Items */}
                            <div>
                              <p className="mb-2 text-xs font-black uppercase tracking-wider text-slate-400">
                                Items ({order.items.length})
                              </p>
                              <ul className="space-y-1.5">
                                {order.items.map((item) => (
                                  <li key={item.id} className="flex items-center justify-between gap-3 text-sm">
                                    <span className="line-clamp-1 text-slate-700 dark:text-slate-300">{item.title}</span>
                                    <span className="shrink-0 text-xs text-slate-500">
                                      ×{item.quantity} · {formatPrice(item.price)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                          </div>
                        </td>
                      </tr>
                    )
                  ].filter(Boolean);
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <AdminPagination
        currentPage={page}
        totalItems={filtered.length}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={(newPerPage) => {
          setPerPage(newPerPage);
          setPage(1);
        }}
        itemLabel="orders"
      />
    </div>
  );
}
